/**
 * Product Gallery -> Variant Picker Sync
 *
 * When the user navigates the MAIN product gallery (scrolling the carousel or
 * clicking a navigation thumbnail), the colour swatch in the <variant-picker>
 * is updated to match the media being shown.
 *
 * The reverse direction (variant picker -> gallery) is handled natively by the
 * theme's <product-gallery> component, which listens for the form's
 * `variant:change` event and scrolls to the variant's featured media.
 *
 * Data source: the <variant-picker> emits a compact variant map as
 * `<script data-variant-availability>` with entries:
 *   { id, options: [<value text per position>], available, media: <featured media id|null> }
 *
 * Behaviour when a colour is selected from the gallery:
 * - Only the colour option is changed; the current size is preserved.
 * - If the current size is sold out for the new colour, the first in-stock size
 *   for that colour is selected instead (avoids dead-end sold-out combinations).
 */

class ProductGalleryVariantSync {
  constructor() {
    this.galleryCleanupMap = new WeakMap();
    this.init();
  }

  init() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    document
      .querySelectorAll("product-gallery")
      .forEach((gallery) => this.setupGallerySync(gallery));

    // Re-run when sections reload in the theme editor.
    if (typeof Shopify !== "undefined" && Shopify.designMode) {
      document.addEventListener("shopify:section:load", () => {
        document
          .querySelectorAll("product-gallery")
          .forEach((gallery) => this.setupGallerySync(gallery));
      });
    }
  }

  setupGallerySync(gallery) {
    if (!gallery) return;
    this.cleanupGallery(gallery);

    const carousel = gallery.querySelector(".product-gallery__carousel");
    if (!carousel) return;

    const formId = gallery.getAttribute("form");
    if (!formId) return;

    const variantPicker = document.querySelector(
      `variant-picker[form-id="${formId}"]`
    );
    if (!variantPicker) return;

    const variants = this.loadVariants(variantPicker);
    if (!variants.length) return;

    const colorIndex = this.getColorOptionIndex(variantPicker);

    const abortController = new AbortController();
    const { signal } = abortController;
    const state = { isUpdatingFromGallery: false };

    const onMediaSelected = (mediaId) => {
      if (state.isUpdatingFromGallery || !mediaId) return;
      const variant = variants.find((v) => String(v.media) === String(mediaId));
      if (!variant) return;
      const colorText = variant.options?.[colorIndex];
      if (colorText == null) return;
      this.selectColor(variantPicker, variants, colorIndex, colorText, state);
    };

    // When the main carousel settles on a new media, sync the colour.
    carousel.addEventListener(
      "carousel:settle",
      (event) => onMediaSelected(event.detail?.cell?.getAttribute("data-media-id")),
      { signal }
    );

    // Navigation thumbnails.
    const galleryNav = gallery.querySelector("product-gallery-navigation");
    if (galleryNav) {
      galleryNav
        .querySelectorAll("button[data-media-id]")
        .forEach((thumbnail) => {
          thumbnail.addEventListener(
            "click",
            () => onMediaSelected(thumbnail.getAttribute("data-media-id")),
            { signal }
          );
        });
    }

    this.galleryCleanupMap.set(gallery, { abortController });
  }

  cleanupGallery(gallery) {
    const cleanup = this.galleryCleanupMap.get(gallery);
    if (cleanup?.abortController) cleanup.abortController.abort();
    this.galleryCleanupMap.delete(gallery);
  }

  /**
   * Parse the variant map emitted by the variant picker.
   */
  loadVariants(variantPicker) {
    try {
      const script = variantPicker.querySelector(
        "script[data-variant-availability]"
      );
      if (!script) return [];
      const parsed = JSON.parse(script.textContent || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.warn("ProductGalleryVariantSync: Could not parse variant data", e);
      return [];
    }
  }

  /**
   * The colour option is the one driven by the variant image carousel. Fall back
   * to the first option when no carousel is present.
   */
  getColorOptionIndex(variantPicker) {
    const carousel = variantPicker.querySelector("variant-image-carousel");
    const index = parseInt(carousel?.dataset.optionIndex ?? "0", 10);
    return Number.isFinite(index) ? index : 0;
  }

  /**
   * Select a colour value in the picker, preserving the current size when the
   * combination is in stock, otherwise falling back to the first available size.
   */
  selectColor(variantPicker, variants, colorIndex, colorText, state) {
    const colorRadios = Array.from(
      variantPicker.querySelectorAll(
        `input[data-option-position="${colorIndex + 1}"]`
      )
    );
    const targetRadio = colorRadios.find(
      (radio) => radio.dataset.optionText === colorText
    );

    // Already selected (or no matching swatch) -> nothing to do.
    if (!targetRadio || targetRadio.checked) return;

    state.isUpdatingFromGallery = true;

    this.ensureAvailableSizeForColor(
      variantPicker,
      variants,
      colorIndex,
      colorText
    );

    targetRadio.checked = true;
    targetRadio.dispatchEvent(new Event("change", { bubbles: true }));

    // Release the guard after the native variant change + gallery scroll settle.
    setTimeout(() => {
      state.isUpdatingFromGallery = false;
    }, 400);
  }

  /**
   * Keep the current size if the (colour, size) combination is in stock; otherwise
   * align the other option radios to the first in-stock variant for this colour.
   */
  ensureAvailableSizeForColor(variantPicker, variants, colorIndex, colorText) {
    if (!variants.length) return;

    const currentByPos = {};
    variantPicker
      .querySelectorAll("input[data-option-position]:checked")
      .forEach((input) => {
        const pos = parseInt(input.dataset.optionPosition, 10) - 1;
        if (Number.isFinite(pos)) currentByPos[pos] = input.dataset.optionText;
      });

    const prospective = { ...currentByPos, [colorIndex]: colorText };

    const matches = (options, combo) =>
      Object.keys(combo).every((pos) => {
        const p = parseInt(pos, 10);
        return combo[p] == null || options[p] === combo[p];
      });

    // Current size works for the new colour -> keep it.
    if (variants.some((v) => v.available && matches(v.options, prospective))) {
      return;
    }

    const firstAvailable = variants.find(
      (v) => v.available && v.options[colorIndex] === colorText
    );
    if (!firstAvailable) return; // whole colour sold out

    firstAvailable.options.forEach((valueText, pos) => {
      if (pos === colorIndex) return;
      const radios = Array.from(
        variantPicker.querySelectorAll(
          `input[data-option-position="${pos + 1}"]`
        )
      );
      const target = radios.find((r) => r.dataset.optionText === valueText);
      if (target && !target.checked) target.checked = true;
    });
  }
}

if (!window.productGalleryVariantSync) {
  window.productGalleryVariantSync = new ProductGalleryVariantSync();
}
